/**
 * Create-task dialog — Phase 1 manual task creation.
 */
import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import api from '../../lib/api';

const TASK_TYPES = [
  ['call', 'Call'], ['follow_up_call', 'Follow-up Call'], ['sms', 'Send Text'], ['email', 'Send Email'],
  ['proposal', 'Send Proposal'], ['statement_review', 'Review Statement'], ['application_follow_up', 'Application Follow-up'],
  ['document_request', 'Request Documents'], ['demo_schedule', 'Schedule Demo'], ['site_visit', 'Site Visit'],
  ['account_review', 'Account Review'], ['other', 'Other'],
];

export function NewTaskModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ title: '', subject_name: '', notes: '', task_type: 'call', priority: 'normal', due_date: '', due_time: '' });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const due = form.due_date ? new Date(`${form.due_date}T${form.due_time || '09:00'}:00`).toISOString() : null;
      await api.createTask({ ...form, due_date: due });
      toast.success('Task created');
      setForm({ title: '', subject_name: '', notes: '', task_type: 'call', priority: 'normal', due_date: '', due_time: '' });
      onCreated();
      onClose();
    } catch (e) {
      toast.error(e.message || 'Failed to create task');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="new-task-modal">
        <DialogHeader><DialogTitle>New Task</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Task title (e.g. Call Bella Napoli)" value={form.title}
            onChange={(e) => set('title', e.target.value)} data-testid="new-task-title-input" />
          <Input placeholder="Related to (business/merchant name, optional)" value={form.subject_name}
            onChange={(e) => set('subject_name', e.target.value)} data-testid="new-task-subject-input" />
          <div className="grid grid-cols-2 gap-3">
            <Select value={form.task_type} onValueChange={(v) => set('task_type', v)}>
              <SelectTrigger data-testid="new-task-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TASK_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={form.priority} onValueChange={(v) => set('priority', v)}>
              <SelectTrigger data-testid="new-task-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['low', 'normal', 'high', 'urgent'].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input type="date" value={form.due_date} onChange={(e) => set('due_date', e.target.value)} data-testid="new-task-due-date-input" />
            <Input type="time" value={form.due_time} onChange={(e) => set('due_time', e.target.value)} data-testid="new-task-due-time-input" />
          </div>
          <Textarea placeholder="Notes (optional)" value={form.notes} onChange={(e) => set('notes', e.target.value)} data-testid="new-task-notes-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="new-task-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="new-task-submit-btn">{saving ? 'Creating…' : 'Create Task'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
